import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { AppShell } from '@mantine/core';
import { useAtom, useAtomValue } from 'jotai';
import { notifications } from '@mantine/notifications';
import { httpFetch } from '@/renderer/utils/http';
import { getApiBase } from '@/renderer/utils/session';
import { getStoredAuthToken } from '@/utils/userApi';
import { ErrorBoundary } from './components';
import { visualStyleAtom } from '@/stores/atoms/themeAtoms';
import { applyVisualStyle } from '@/shared/visualTheme';
import { darkMono, lightMono } from '@/shared/visualTheme';
import ChatHistory from './components/chat/ChatHistory';
import ChatOverlay from './components/chat/ChatOverlay';
import CreateNovel from './pages/CreateNovel';
import GameUIDesignerOverlay from './components/create/GameUIDesignerOverlay';
import BottomNav from './components/mobile/BottomNav';
import MineDrafts from './pages/MineDrafts';
import AuthModal from './components/auth/AuthModal';
import AuthProvider from './components/providers/AuthProvider';
import DataPersistenceProvider from './components/providers/DataPersistenceProvider';
import NetworkMonitor from './components/providers/NetworkMonitor';
import BackButtonProvider from './components/providers/BackButtonProvider';
import Home from './pages/Home';
import Search from './pages/Search';
import Settings from './pages/Settings';
import SettingsApp from './pages/SettingsApp';
import ProfileNovel from './pages/ProfileNovel';
import ProfileApp from './pages/ProfileApp';
import WhatsApp from './pages/WhatsApp';
import { errorStateAtom, themeAtom, activePageAtom } from './stores/atoms';
import { currentScriptIdAtom } from '@/stores/atoms/scriptAtoms';
import { chatOverlayOpenAtom, chatOverlayReturnAtom } from '@/stores/atoms/uiAtoms';
import { homeShowGamesAtom, homeBackgroundImageAtom } from '@/stores/atoms/uiAtoms';
import { authUserAtom, authReadyAtom, authModalOpenAtom } from '@/stores/atoms/authAtoms';
import { wsManager } from '@/utils/wsManager';
import { rechargeOverlayOpenAtom } from '@/stores/atoms/walletAtoms';
import homeBgDefaultPng from '../../assets/background.png';
function App() {
    const errorState = useAtomValue(errorStateAtom);
    const theme = useAtomValue(themeAtom);
    const visualStyle = useAtomValue(visualStyleAtom);
    const [activePage] = useAtom(activePageAtom);
    const [, setActivePage] = useAtom(activePageAtom);
    const [, setCurrentScriptId] = useAtom(currentScriptIdAtom);
    const [, setOverlayOpen] = useAtom(chatOverlayOpenAtom);
    const [overlayOpen] = useAtom(chatOverlayOpenAtom);
    const [, setOverlayReturn] = useAtom(chatOverlayReturnAtom);
    const [homeShowGames, setHomeShowGames] = useAtom(homeShowGamesAtom);
    const [homeBg] = useAtom(homeBackgroundImageAtom);
    const [authUser] = useAtom(authUserAtom);
    const [authReady] = useAtom(authReadyAtom);
    const [, setAuthModalOpen] = useAtom(authModalOpenAtom);
    const [, setRechargeOpen] = useAtom(rechargeOverlayOpenAtom);
    // 设置主题与视觉变量（统一全局默认底色）
    useEffect(() => {
        const root = document.documentElement;
        const applyByMode = (isDark) => {
            try {
                applyVisualStyle(isDark ? darkMono : lightMono);
            }
            catch { }
            if (isDark)
                root.classList.add('dark-theme');
            else
                root.classList.remove('dark-theme');
        };
        if (theme === 'dark') {
            applyByMode(true);
            return;
        }
        if (theme === 'light') {
            applyByMode(false);
            return;
        }
        // auto mode - 跟随系统
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        applyByMode(!!media.matches);
        const handler = (e) => applyByMode(!!e.matches);
        media.addEventListener('change', handler);
        return () => media.removeEventListener('change', handler);
    }, [theme]);
    // 全局键盘快捷键（预留 ESC 等）
    useEffect(() => {
        // iOS platform flag for CSS targeting (only affects iOS)
        try {
            const ua = navigator.userAgent || '';
            const isIOS = /iPad|iPhone|iPod/.test(ua) || window?.Capacitor?.platform === 'ios';
            if (isIOS)
                document.documentElement.classList.add('ios');
        }
        catch { }
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                // TODO: 关闭模态或覆盖层
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        const handleBeforeUnload = () => {
            try {
                wsManager.close();
            }
            catch { }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);
    // Parse share link (web) on initial load: /?script=<id>
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search || '');
            const script = params.get('script') || params.get('script_id') || undefined;
            if (script) {
                setCurrentScriptId(script);
                setActivePage('chat');
                setOverlayReturn('home');
                setOverlayOpen(true);
                // Require login to start gameplay
                if (authReady && !authUser) {
                    setAuthModalOpen(true);
                }
                // Optional: clean URL so reload doesn't reuse token
                try {
                    const url = new URL(window.location.href);
                    url.searchParams.delete('script');
                    url.searchParams.delete('script_id');
                    window.history.replaceState({}, document.title, url.toString());
                }
                catch { }
            }
        }
        catch { }
    }, [setCurrentScriptId, setActivePage, setOverlayOpen, setOverlayReturn, authReady, authUser, setAuthModalOpen]);
    // Ensure WS only lives on novel reading page; close elsewhere
    // Only rely on overlay visibility to manage WS lifecycle. Active page may remain 'home' under overlay.
    useEffect(() => {
        if (!overlayOpen) {
            try {
                wsManager.close();
            }
            catch { }
        }
    }, [overlayOpen]);
    // All platforms: on fresh entry, default to Home (unless opened by share link)
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search || '');
            const hasShare = !!(params.get('script') || params.get('script_id'));
            if (!hasShare) {
                setActivePage('home');
                setOverlayOpen(false);
                try {
                    setHomeShowGames(false);
                }
                catch { }
            }
        }
        catch { }
        // only run once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Detect payment success via /creem?status=success and notify user
    useEffect(() => {
        try {
            const url = new URL(window.location.href);
            if (url.pathname === '/creem') {
                const status = (url.searchParams.get('status') || '').toLowerCase();
                if (status === 'success') {
                    notifications.show({ color: 'green', title: '充值成功', message: '会员已开通或续期', withCloseButton: true });
                    try {
                        setRechargeOpen(false);
                    }
                    catch { }
                }
                // Clean URL back to app root to avoid re-triggering on reload
                try {
                    url.pathname = '/';
                    url.searchParams.delete('status');
                    url.searchParams.delete('src');
                    window.history.replaceState({}, document.title, url.toString());
                }
                catch { }
            }
        }
        catch { }
    }, [setRechargeOpen]);
    // Daily diamonds grant: call once per day when user is logged in
    useEffect(() => {
        const maybeGrant = async () => {
            try {
                if (!authReady)
                    return;
                if (!authUser)
                    return;
                const token = getStoredAuthToken();
                if (!token)
                    return;
                const now = new Date();
                const dayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                const storeKey = 'diamonds_daily_grant_day';
                const stamped = localStorage.getItem(storeKey);
                if (stamped === dayKey)
                    return; // already granted today
                const base = getApiBase();
                const url = `${base}/api/diamonds/daily_grant`;
                const res = await httpFetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({}),
                    timeoutMs: 10000,
                });
                let data = undefined;
                try {
                    data = await res.json();
                }
                catch { }
                if (res.ok && data && data.success === true) {
                    try {
                        localStorage.setItem(storeKey, dayKey);
                    }
                    catch { }
                }
                // If not success, do not stamp; will retry on next app entry
            }
            catch { }
        };
        maybeGrant();
    }, [authReady, authUser]);
    // 如果有错误，显示错误页面
    if (errorState.hasError) {
        return (_jsx(ErrorBoundary, { error: errorState.error, errorInfo: errorState.errorBoundaryInfo }));
    }
    // Only show novel bottom nav on novel pages
    const isNovelHome = activePage === 'home' && homeShowGames;
    const isNovelSubPage = activePage === 'search' || activePage === 'chat' || activePage === 'mine';
    const isNovelPage = isNovelHome || isNovelSubPage;
    return (_jsx("div", { className: "app", style: { minHeight: '100dvh', background: 'var(--app-bg-primary)' }, children: _jsx(AuthProvider, { children: _jsx(DataPersistenceProvider, { children: _jsxs(AppShell, { padding: 0, footer: { height: isNovelPage ? 56 : 0 }, style: { height: '100dvh', display: 'flex', flexDirection: 'column' }, children: [_jsxs(AppShell.Main, { style: {
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                position: 'relative',
                                background: (activePage === 'home' && !homeShowGames) ? `url(${homeBg || homeBgDefaultPng}) center/cover no-repeat` : undefined,
                            }, children: [activePage === 'home' ? (_jsx(Home, {})) : activePage === 'search' ? (_jsx(Search, {})) : activePage === 'profile' ? ((homeShowGames ? _jsx(ProfileNovel, {}) : _jsx(ProfileApp, {}))) : activePage === 'settings' ? ((homeShowGames ? _jsx(Settings, {}) : _jsx(SettingsApp, {}))) : activePage === 'create' ? (_jsx(CreateNovel, {})) : activePage === 'whatsapp' ? (_jsx(WhatsApp, {})) : activePage === 'mine' ? (_jsx(MineDrafts, {})) : (_jsx(ChatHistory, {})), _jsx(ChatOverlay, {}), _jsx(GameUIDesignerOverlay, {}), _jsx(NetworkMonitor, {}), _jsx(BackButtonProvider, {})] }), isNovelPage && (_jsx(AppShell.Footer, { style: { paddingBottom: 'env(safe-area-inset-bottom)' }, children: _jsx(BottomNav, {}) })), _jsx(AuthModal, {})] }) }) }) }));
}
export default App;
